import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from 'react-helmet';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ResiduesTable from '../../components/ResiduesTable/ResiduesTable';
import ContactTable from '../../components/ContactTable/ContactTable';
import GraphlqlQuery from '../../components/GraphlqlQuery/GraphlqlQuery';
import BarChartFunctional from '../../components/BarChartFunctional/BarChartFunctional';
import FunctionalAnnotationStructureLegend from '../../components/FunctionalAnnotationStructureLegend/FunctionalAnnotationStructureLegend';
import { Divider } from 'primereact/divider';
import { Tooltip } from 'primereact/tooltip';
import 'animate.css';
import 'primeicons/primeicons.css';
import { getLigand, getSeqFeatures, getVariants, getPDBsFromUniprot, getPDB2Uniprot, sendExploreData, createDomainsColors, getInterProDomains, getOverview, getStructureContact} from '../../ApiFunctions.js';
import { TabView, TabPanel } from 'primereact/tabview';
import MolstarViewerFunctional from '../../components/MolstarViewerFunctional/MolstarViewerFunctional';
import { Link } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ListBox } from 'primereact/listbox';
import { Message } from 'primereact/message';
import OverviewSection from '../../components/OverviewSection/OverviewSection';
import ResidueSection from '../../components/ResidueSection/ResidueSection';
import TitleResultPage from '../../components/TitleResultPage/TitleResultPage';
import LigandSection from '../../components/LigandSection/LigandSection';
import IntraContactSection from '../../components/IntraContactSection/IntraContactSection';
import InterContactSection from '../../components/InterContactSection/InterContactSection';
import VariantSection from '../../components/VariantSection/VariantSection';
import PathfinderAnalysisSection from '../../components/PathfinderAnalysisSection/PathfinderAnalysisSection';


// Chave usada para representar a chamada "full" (kind === "")
const FULL_KEY = "full";

const ResultPage = () => {

const navigate = useNavigate();
const location = useLocation();
const [loadFunctionalInfo, setLoadFunctionalInfo] = useState(false);
const [overviewLoadData, setOverviewLoadData] = useState(false);
const [overviewData, setOverview] = useState([]);
const [activeTabIndex, setActiveTabIndex] = useState(0);
const [seqLength, setSeqLength] = useState(0);

const [target, setTarget] = useState("");
const [type, setType] = useState("");
const [info, setInfo] = useState([]);

useEffect(() => {
    const query = new URLSearchParams(location.search);
    const urlTarget = query.get('target');
    const urlType = query.get('type');

    const isFunctionalType = (type) => type === "residue" || type === "gene_residue";

    const fetchData = async (target, type) => {
        try {
            const [dataResponse, overviewData] = await Promise.all([
                sendExploreData(target, type),
                getOverview(target)
            ]);

            const data = dataResponse?.data || [];
            setInfo(data);
            setOverview(overviewData || []);

        } catch (error) {
            console.error('Error fetching data:', error);
            setInfo([]);
            setOverview([]);
        } finally {
            isFunctionalType(type) ? setLoadFunctionalInfo(false) : setOverviewLoadData(false);
        }
    };

    const initializeFromUrl = () => {
        if (!urlTarget || !urlType) {
            navigate('/');
            return;
        }

        setTarget(urlTarget);
        setType(urlType);

        isFunctionalType(urlType) ? setLoadFunctionalInfo(true) : setOverviewLoadData(true);
        fetchData(urlTarget, urlType);
    };

    const initializeFromState = () => {
        const state = location.state;
        setTarget(state.target || "");
        setType(state.type || "");
        setInfo(state.data || []);
        setOverview(state.overview || []);
        isFunctionalType(urlType) ? setLoadFunctionalInfo(false) : setOverviewLoadData(false);
    };

    if (location.state) {
        initializeFromState();
    } else {
        initializeFromUrl();
    }

    if (isFunctionalType(type)) {
        setActiveTabIndex(1);
    }

}, [location.search, location.state, navigate, type]);


const residueGeneData =  [info.residueByGeneSymbolAndPosition] || [];
const datatable = info.residues ||  info.residuesByGeneSymbol ||  [info.residue]  || [];

// Setting data tab
const [ligandData, setLigandData] = useState([]);
const [variantData, setVariantData] = useState([]);

// Dados de contato de estrutura, separados por "kind" (intra / inter / full)
// para que cada aba tenha seu próprio cache e não sobrescreva o das outras.
const [contactStructureData, setContactStructureData] = useState({
    intra: [],
    inter: [],
    [FULL_KEY]: []
});

// Setting load icon
const [ligandLoadData, setLigandLoadData] = useState(false);
const [variantLoadData, setVariantLoadData] = useState(false);

// Loading de contato de estrutura, também separado por "kind"
const [structureContactLoadData, setStructureContactLoadData] = useState({
    intra: false,
    inter: false,
    [FULL_KEY]: false
});

// Control Calls
const [callNumberLig, setCallNumberLig] = useState(1);
const [callNumberVar, setCallNumberVar] = useState(1);
const [callNumberOve, setCallNumberOve] = useState(1);
const [callNumberPath, setCallNumberPath] = useState(1);


// get data when click in tab's name
useEffect(() => {

    if (activeTabIndex === 2 && contactStructureData.intra?.length === 0) {
         fetchStructureContact("intra");
    }

    if (activeTabIndex === 3 && contactStructureData.inter?.length === 0) {
         fetchStructureContact("inter");
    }

    if (activeTabIndex === 4 && callNumberLig === 1) {
        fetchLigandData();
    }

    if (activeTabIndex === 5 && callNumberVar === 1) {
        fetchVariantData();
    }

    if (activeTabIndex === 6 && contactStructureData[FULL_KEY]?.length === 0) {
       fetchStructureContact("");
    }

}, [activeTabIndex, callNumberVar, callNumberLig]);


// Get ligand data
const fetchLigandData = async () => {
    if (!target) return;

    setLigandLoadData(true);
    try {
      const data = await getLigand(target);
      if (Array.isArray(data) && data.length > 0) {

               const ligands = data.flatMap(residue =>
                    (residue.ligands && residue.ligands.length > 0) ? residue.ligands.map(ligand => ({
                        ...ligand,
                        anPosition: residue.anPosition,
                        summary: residue.summary
                    })) : []
                );
        setLigandData(ligands);
      }
    } catch (error) {
      console.error("Error fetching ligand data:", error);
    } finally {
      setLigandLoadData(false);
      setCallNumberLig(callNumberLig + 1);
    }
 };

// Get variant data
const fetchVariantData = async () => {
    if (!target) return;

    setVariantLoadData(true);

    try {
            const ClinVarData = await getVariants(target);
            if (ClinVarData && Array.isArray(ClinVarData) && ClinVarData.length > 0) {
                const variantDta = ClinVarData.flatMap(residue =>
                    (residue.clinvarVariants && residue.clinvarVariants.length > 0)
                        ? residue.clinvarVariants.map(variant => ({
                            ...variant,
                            anPosition: residue.anPosition,
                            unDomains: residue.unDomains,
                            unLigandsBindingSite: residue.unLigandsBindingSite,
                            unAnnPropActiveSite: residue.unAnnPropActiveSite,
                            phosPtms: residue.phosPtms,
                            afMaxPredictedNumInteractionResidue: residue.afMaxPredictedNumInteractionResidue,
                            inBindingRegion: residue.inBindingRegion,
                            interproDomain: residue.interproDomain,
                            pfamDomainPosHmm: residue.pfamDomainPosHmm,
                            summary: residue.summary
                        }))
                        : []
                );
                setVariantData(variantDta);

            }
    } catch (error) {
        console.error("Error fetching variant data:", error);
    } finally {
        setVariantLoadData(false);
        setCallNumberVar(callNumberVar + 1);
    }
};

// Get structure from contacts
// kind: "intra" | "inter" | "" (full). Cada kind grava seu próprio slot
// no state, então as três abas não pisam mais uma na outra.
const fetchStructureContact = async (kind) => {

    if (!target) return;

    const key = kind || FULL_KEY;

    setStructureContactLoadData(prev => ({ ...prev, [key]: true }));

    try {

      const data = await getStructureContact(target, kind);
      setContactStructureData(prev => ({ ...prev, [key]: data || [] }));

    } catch (error) {
      console.error("Error fetching intra-protein contact data:", error);
    } finally {
      setStructureContactLoadData(prev => ({ ...prev, [key]: false }));
      setCallNumberPath(callNumberPath + 1);
    }
};


return (
    <>
      <Helmet>
        <title> Atlantis | {target}</title>
      </Helmet>
      <Header />
      <section className="py-9">
        <div className="container">
         <TitleResultPage target={target} type={type}/>
        <TabView scrollable activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)} renderActiveOnly={false}>
        {(type === "gene" || type === "protein") &&  (
        <TabPanel header="Overview">
               <OverviewSection target={target}  data={overviewData}  setActiveTabIndex={setActiveTabIndex} />
        </TabPanel>
        )}
        <TabPanel header="Functional site information">
               <ResidueSection datatable={datatable}  loadFunctionalInfo={loadFunctionalInfo}  type={type} target={target}  activeTabIndex={activeTabIndex}  residueGeneData={residueGeneData}  setActiveTabIndex={setActiveTabIndex}/>
            </TabPanel>
           <TabPanel header="Intra-protein contacts">
               <IntraContactSection type={type} target={target} contactStructureData={contactStructureData.intra} contactStructureLoading={structureContactLoadData.intra} activeTabIndex={activeTabIndex} />
          </TabPanel>
          <TabPanel header="Inter-protein contacts">
              <InterContactSection activeTabIndex={activeTabIndex} type={type} target={target}  contactStructureData={contactStructureData.inter} contactStructureLoading={structureContactLoadData.inter}/>
          </TabPanel>
          <TabPanel header="Ligands">
               <LigandSection activeTabIndex={activeTabIndex} type={type} target={target}  ligandData={ligandData} ligandLoadData={ligandLoadData}/>
           </TabPanel>
         <TabPanel header="ClinVar">
              <VariantSection activeTabIndex={activeTabIndex} type={type} target={target} datatable={datatable}  variantData={variantData}  variantLoadData={variantLoadData}  seqLength={seqLength}/>
         </TabPanel>
          {(type === "gene" || type === "protein") &&  (
            <TabPanel header="Pathfinder&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;">
                 <PathfinderAnalysisSection  target={target} contactStructureData={contactStructureData[FULL_KEY]} contactStructureLoading={structureContactLoadData[FULL_KEY]} />
            </TabPanel>
         )}
        </TabView>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default ResultPage;